import { muiDataGridStarted } from "../apiutils";
import { apiSlice } from "../createApiSlice";

const INVOICE_BASE = "/invoices";
const buildUrl = (endpoint) => INVOICE_BASE + endpoint;
const API_TAG = 'Invoices';
const LOGS_TAG = 'LogsInvoice';
const CONCEPT_INVOICEPRODUCTS_TAG = 'ConceptInvoiceProducts';

const invoiceSlice =  apiSlice.injectEndpoints({
    endpoints: builder => ({
        getInvoices: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: API_TAG }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getInvoices', API_TAG)
        }),
        getInvoice: builder.query({
            query: (id) => ({
                url: buildUrl(`/${id}`),
                method: 'GET',
            }),
            providesTags: (result, error, arg) => [{ type: `${API_TAG}Single`}]
        }),
        createConceptInvoice: builder.mutation({
            query: invoiceInfo => ({
                url: buildUrl(`/concept`),
                method: 'POST',
                body: invoiceInfo,
            }),
            invalidatesTags: (result, error, arg) => [{ type: API_TAG }]
        }),
        addInvoice: builder.mutation({
            query: invoiceInfo => ({
                url: buildUrl(`/new`),
                method: 'POST',
                body: invoiceInfo,
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG}],
        }),
        updateInvoice: builder.mutation({
            query: invoiceInfo => ({
                url: buildUrl(`/${invoiceInfo.id}`),
                method: 'PUT',
                body: invoiceInfo,
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`}],
        }),
        recalculateInvoice: builder.mutation({
            query: (id) => ({
                url: buildUrl(`/recalculate/${id}`),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`}],
        }),
        finalizeInvoice: builder.mutation({
            query: (id) => ({
                url: buildUrl(`/finalize/${id}`),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`}],
        }),
        revertInvoiceToConcept: builder.mutation({
            query: (id) => ({
                url: buildUrl(`/revert-to-concept/${id}`),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`}],
        }),
        finalizeAllInvoices: builder.mutation({
            query: (id) => ({
                url: buildUrl(`/finalizeall`),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }],
        }),
        finalizeAllInvoices: builder.mutation({
            query: (id) => ({
                url: buildUrl(`/finalizeall`),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }],
        }),
        markInvoicesPaid: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/markpaid`),
                method: 'PUT',
                body: updateObject,
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`}],
        }),
        sendInvoiceReminders: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/reminders`),
                method: 'PUT',
                body: updateObject,
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`}],
        }),
        finalizeInvoiceInvoiceProducts: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/invoiceproducts/finalize`),
                method: 'PUT',
                body: updateObject,
            }),
            invalidatesTags: (result, error, arg) => [{type: CONCEPT_INVOICEPRODUCTS_TAG }, { type: API_TAG }, { type: "InvoiceMutations" }],
        }),
        declineInvoiceInvoiceProducts: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/invoiceproducts/decline`),
                method: 'PUT',
                body: updateObject,
            }),
            invalidatesTags: (result, error, arg) => [{type: CONCEPT_INVOICEPRODUCTS_TAG }, { type: API_TAG }],
        }),
        exportExcel: builder.mutation({
            query: (filterParams) => ({
                url: buildUrl(`/${filterParams.id}/invoiceproducts/export${filterParams.queryString ? filterParams.queryString : ""}`),
                method: 'GET',
                responseHandler: async (response) => {
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }

                    const contentType = response.headers.get('content-type');

                    // Check if response is JSON (status 202 - processing or 200 with blobUrl)
                    if (contentType && contentType.includes('application/json')) {
                        const jsonData = await response.json();

                        // If immediate download is ready (status 200 with blobUrl)
                        if (jsonData.blobUrl) {
                            // For storage URLs or signed URLs, just trigger direct download
                            // No need to fetch again - the URL is already accessible
                            const a = document.createElement('a');
                            a.href = jsonData.blobUrl;
                            a.download = jsonData.fileName || `invoicemutations_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                            a.target = '_blank'; // Open in new tab as fallback
                            document.body.appendChild(a);
                            a.click();
                            a.remove();

                            return { success: true, message: jsonData.message, immediate: true };
                        }

                        // Background processing (status 202)
                        return { success: true, processing: true, message: jsonData.message, exportId: jsonData.exportId };
                    }

                    // Legacy support: Direct blob response (for backwards compatibility)
                    const blob = await response.blob();

                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `invoicemutations_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);

                    return { success: true, immediate: true };
                },
            }),
        }),
        exportInvoices: builder.mutation({
            query: filterParams => {
                return {
                  url: buildUrl(`/export`),
                  method: 'POST',
                  body: filterParams,
                  responseHandler: async (response) => {
                        if (!response.ok) {
                            throw new Error('Network response was not ok');
                        }

                        const contentType = response.headers.get('content-type');

                        // Check if response is JSON (status 202 - processing or 200 with blobUrl)
                        if (contentType && contentType.includes('application/json')) {
                            const jsonData = await response.json();

                            // If immediate download is ready (status 200 with blobUrl)
                            if (jsonData.blobUrl) {
                                // For storage URLs or signed URLs, just trigger direct download
                                // No need to fetch again - the URL is already accessible
                                const a = document.createElement('a');
                                a.href = jsonData.blobUrl;
                                a.download = jsonData.fileName || `products_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                                a.target = '_blank'; // Open in new tab as fallback
                                document.body.appendChild(a);
                                a.click();
                                a.remove();

                                return { success: true, message: jsonData.message, immediate: true };
                            }

                            // Background processing (status 202)
                            return { success: true, processing: true, message: jsonData.message, exportId: jsonData.exportId };
                        }

                        // Legacy support: Direct blob response (for backwards compatibility)
                        const blob = await response.blob();

                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `invoices_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);

                        return { success: true, immediate: true };
                    },
                };
              },
        }),
        deleteInvoice: builder.mutation({
            query: (id) => ({
                url: buildUrl(`/${id}`),
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`}],
        }),
        getConceptInvoiceInvoiceProducts: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/invoiceproducts/concepts?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: CONCEPT_INVOICEPRODUCTS_TAG }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getConceptInvoiceInvoiceProducts', CONCEPT_INVOICEPRODUCTS_TAG)
        }),
        getConceptInvoiceInvoiceProductsCount: builder.query({
            query: () => ({
                url: buildUrl(`/invoiceproducts/concepts/count`),
                method: 'GET',
            }),
            providesTags: () => [{ type: "NoCostsCount" }],
        }),
        getConceptInvoiceInvoiceProduct: builder.query({
            query: (id) => ({
                url: buildUrl(`/invoiceproducts/concepts/${id}`),
                method: 'GET',
            }),
            providesTags: (result, error, arg) => [{ type: CONCEPT_INVOICEPRODUCTS_TAG }],
        }),
        updateConceptInvoiceInvoiceProduct: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/invoiceproducts/concepts/${updateObject.id}`),
                method: 'PUT',
                body: updateObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: CONCEPT_INVOICEPRODUCTS_TAG }],
        }),
        deleteConceptInvoiceInvoiceProduct: builder.mutation({
            query: (id) => ({
                url: buildUrl(`/invoiceproducts/concepts/${id}`),
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, arg) => [{ type: CONCEPT_INVOICEPRODUCTS_TAG }, {type: API_TAG }, { type: `${API_TAG}Single`}],
        }),
        getInvoiceInvoiceProducts: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/${args.id}/invoiceproducts?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: (result, error, arg) => [{ type: `${API_TAG}Single`}],
            onQueryStarted: muiDataGridStarted.bind(null, 'getInvoiceLines', `${API_TAG}Single`)
        }),
        addConceptInvoiceInvoiceProduct: builder.mutation({
            query: invoiceProductsInfo => ({
                url: buildUrl(`/invoicesingleproduct`),
                method: 'POST',
                body: invoiceProductsInfo,
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`}, {type: LOGS_TAG}],
        }),
        addInvoiceInvoiceProducts: builder.mutation({
            query: invoiceProductsInfo => ({
                url: buildUrl(`/invoiceproducts/new`),
                method: 'POST',
                body: invoiceProductsInfo,
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`}, {type: LOGS_TAG}, {type: "OrdersSingle"}, {type: "OrdersWithoutCosts"}, { type: `InboundSingle` }, { type: 'ReturnsSingle'}, { type: 'Inbounds' }],
        }),
        deleteInvoiceInvoiceProducts: builder.mutation({
            query: deleteObject => ({
                url: buildUrl(`/${deleteObject.invoiceId}/invoiceproducts/`),
                method: 'DELETE',
                body: deleteObject.invoiceProductIds,
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`}, {type: LOGS_TAG}],
        }),
        updateInvoiceInvoiceProducts: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/${updateObject.invoiceId}/invoiceproducts`),
                method: 'PUT',
                body: updateObject.data,
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`}, {type: LOGS_TAG}],
        }),
        getInvoiceLines: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/${args.id}/invoicelines?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: (result, error, arg) => [{ type: `${API_TAG}Single`}],
            onQueryStarted: muiDataGridStarted.bind(null, 'getInvoiceLines', `${API_TAG}Single`)
        }),
        createCreditInvoice: builder.mutation({
            query: ({ id, invoiceLineIds, invoiceDate }) => ({
                url: buildUrl(`/${id}/credit`),
                method: 'POST',
                body: { invoiceLineIds, invoiceDate },
            }),
            invalidatesTags: (result, error, arg) => [{ type: API_TAG }, { type: `${API_TAG}Single`}],
        }),
        linkedObjectUpdateProducts: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/linkedobject/invoiceproducts/${updateObject.linkedId}`),
                method: 'PUT',
                body: updateObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: "OrdersSingle" }],
        }),
        getOrdersWithoutSendingCosts: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/orders/withoutsending?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: "OrdersWithoutCosts" }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getOrderWithoutCosts', "OrdersWithoutCosts")
        }),
        recalculateOrderCosts: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/orders/recalculate`),
                method: 'PUT',
                body: updateObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: "OrdersWithoutCosts" }, {type: 'NoCostsCount'}],
        }),
        calculateShipmentCosts: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/shipments/calculate`),
                method: 'PUT',
                body: updateObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: "OrdersWithoutCosts" }, {type: 'NoCostsCount'}, { type: 'Shipments' }],
        }),
        calculateReturnCosts: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/returns/calculate`),
                method: 'PUT',
                body: updateObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: "OrdersWithoutCosts" }, {type: 'NoCostsCount'}, { type: 'ReturnsWithoutCosts' }],
        }),
        calculateInvoiceLines: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/${updateObject.id}/invoicelines/recalculate/`),
                method: 'PUT',
                body: updateObject,
            }),
            invalidatesTags: (result, error, arg) => [{type: API_TAG }, { type: `${API_TAG}Single`}],
        }),
        updateProductPriceConceptInvoices: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/invoiceproducts/update/price`),
                method: 'PUT',
                body: updateObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: API_TAG }, { type: "InvoiceMutations" }],
        }),
        updateProductPriceConceptInvoices: builder.mutation({
            query: updateObject => ({
                url: buildUrl(`/invoiceproducts/update/price`),
                method: 'PUT',
                body: updateObject,
            }),
            invalidatesTags: (result, error, arg) => [{ type: API_TAG }, { type: "InvoiceMutations" }],
        }),
        getReturnsWithoutSendingCosts: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/returns/withoutsending?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: "ReturnsWithoutCosts" }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getReturnWithoutCosts', "ReturnsWithoutCosts")
        }),
        getShippingCostOverview: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/shippingcosts/overview?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: "ShippingCostOverview" }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getShippingCostOverview', "ShippingCostOverview")
        }),
        // The cards summarise the whole period while the grid pages through it, so the
        // totals are read separately with the smallest page the endpoint will serve.
        getShippingCostOverviewTotals: builder.query({
            query: (body) => ({
                url: buildUrl(`/shippingcosts/overview?page=1&size=1`),
                method: 'POST',
                body: body,
            }),
            providesTags: () => [{ type: "ShippingCostOverview" }],
        }),
        getInvoiceMutations: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/mutations?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: "InvoiceMutations" }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getInvoices', "InvoiceMutations")
        }),
        checkOrderInvoiceProducts: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/orders/checkproducts`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: "InvoiceMutations" }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getInvoices', "InvoiceMutations")
        }),
        checkOrderCostRule: builder.mutation({
            query: ({ orderId, priority }) => ({
                url: buildUrl(`/orders/checkcostrule`),
                method: 'POST',
                body: { orderId, priority },
            }),
        }),
        downloadInvoicePDF: builder.mutation({
            // type "reminder" serves the stored reminder copy instead of the original invoice.
            query: ({ id, type }) => ({
                url: buildUrl(`/${id}/download${type ? `?type=${type}` : ""}`),
                method: 'GET',
                responseHandler: async (response) => {
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }

                    const blob = new Blob([await response.blob()], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    return {data: url};
                },
            }),
        }),
    })
});

export const {
    useGetInvoicesQuery,
    useGetInvoiceInvoiceProductsQuery,
    useGetConceptInvoiceInvoiceProductsQuery,
    useGetConceptInvoiceInvoiceProductsCountQuery,
    useAddConceptInvoiceInvoiceProductMutation,
    useAddInvoiceInvoiceProductsMutation,
    useGetInvoiceQuery,
    useExportInvoicesMutation,
    useCreateConceptInvoiceMutation,
    useGetConceptInvoiceInvoiceProductQuery,
    useUpdateConceptInvoiceInvoiceProductMutation,
    useDeleteConceptInvoiceInvoiceProductMutation,
    useAddInvoiceMutation,
    useUpdateInvoiceMutation,
    useRecalculateInvoiceMutation,
    useFinalizeInvoiceMutation,
    useRevertInvoiceToConceptMutation,
    useFinalizeAllInvoicesMutation,
    useMarkInvoicesPaidMutation,
    useSendInvoiceRemindersMutation,
    useFinalizeInvoiceInvoiceProductsMutation,
    useDeclineInvoiceInvoiceProductsMutation,
    useDeleteInvoiceMutation,
    useDeleteInvoiceInvoiceProductsMutation,
    useUpdateInvoiceInvoiceProductsMutation,
    useGetInvoiceLinesQuery,
    useCreateCreditInvoiceMutation,
    useExportExcelMutation,
    useLinkedObjectUpdateProductsMutation,
    useGetOrdersWithoutSendingCostsQuery,
    useRecalculateOrderCostsMutation,
    useGetReturnsWithoutSendingCostsQuery,
    useGetInvoiceMutationsQuery,
    useGetShippingCostOverviewQuery,
    useGetShippingCostOverviewTotalsQuery,
    useUpdateProductPriceConceptInvoicesMutation,
    useCheckOrderInvoiceProductsQuery,
    useCheckOrderCostRuleMutation,
    useCalculateInvoiceLinesMutation,
    useDownloadInvoicePDFMutation,
    useCalculateShipmentCostsMutation,
    useCalculateReturnCostsMutation
  } = invoiceSlice;
